import { useState } from 'react'
import Tabs from '@cloudscape-design/components/tabs'
import Table from '@cloudscape-design/components/table'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Modal from '@cloudscape-design/components/modal'
import Pagination from '@cloudscape-design/components/pagination'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Textarea from '@cloudscape-design/components/textarea'
import TextFilter from '@cloudscape-design/components/text-filter'
import Header from '@cloudscape-design/components/header'
import FormField from '@cloudscape-design/components/form-field'
import PageHeader from '@/components/common/PageHeader'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  useAdminUsers,
  useWaitlist,
  useApproveWaitlist,
  useDisableUser,
  useSystemPrompts,
  useUpsertSystemPrompt,
  useDeleteSystemPrompt,
} from '@/hooks/useAdmin'
import type { UserAccount, WaitlistEntry, SystemPrompt } from '@/lib/types'

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const { users, loading } = useAdminUsers()
  const { disableUser, loading: disableLoading } = useDisableUser()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const { items, filterProps, paginationProps, collectionProps } = useCollection<UserAccount>(
    users,
    {
      filtering: {
        empty: <Box textAlign="center">No users found.</Box>,
        noMatch: <Box textAlign="center">No users match the filter.</Box>,
      },
      pagination: { pageSize: isMobile ? 10 : 20 },
      sorting: {},
    },
  )

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        <Cards<UserAccount>
          {...collectionProps}
          loading={loading}
          loadingText="Loading users"
          items={items}
          cardsPerRow={[{ cards: 1 }]}
          header={<Header counter={`(${users.length})`}>Users</Header>}
          cardDefinition={{
            header: (item) => (
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Box variant="strong">{item.email}</Box>
                {item.disabled_at ? (
                  <Badge color="red">Disabled</Badge>
                ) : (
                  <Badge color="green">Active</Badge>
                )}
              </SpaceBetween>
            ),
            sections: [
              {
                id: 'plan',
                header: 'Plan',
                content: (item) => (
                  <ColumnLayout columns={3} variant="text-grid">
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Plan</Box>
                      <Box>{item.plan_type}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Clients Used</Box>
                      <Box>{item.max_clients}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Runs This Month</Box>
                      <Box>{item.runs_this_month ?? 0}</Box>
                    </SpaceBetween>
                  </ColumnLayout>
                ),
              },
              {
                id: 'created',
                header: 'Created At',
                content: (item) => new Date(item.created_at).toLocaleDateString(),
              },
              {
                id: 'actions',
                header: 'Actions',
                content: (item) => (
                  <Button
                    variant="inline-link"
                    loading={disableLoading}
                    onClick={() => disableUser(item.uid, !item.disabled_at)}
                  >
                    {item.disabled_at ? 'Enable' : 'Disable'}
                  </Button>
                ),
              },
            ],
          }}
          filter={
            <TextFilter
              {...filterProps}
              filteringPlaceholder="Search users"
            />
          }
          empty={<Box textAlign="center">No users.</Box>}
        />
        <Pagination {...paginationProps} />
      </SpaceBetween>
    )
  }

  return (
    <Table<UserAccount>
      {...collectionProps}
      loading={loading}
      loadingText="Loading users"
      items={items}
      columnDefinitions={[
        {
          id: 'email',
          header: 'Email',
          cell: (item) => item.email,
          sortingField: 'email',
        },
        {
          id: 'plan',
          header: 'Plan',
          cell: (item) => item.plan_type,
          sortingField: 'plan_type',
        },
        {
          id: 'max_clients',
          header: 'Clients Used',
          cell: (item) => item.max_clients,
        },
        {
          id: 'runs_this_month',
          header: 'Runs This Month',
          cell: (item) => item.runs_this_month ?? 0,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) =>
            item.disabled_at ? (
              <Badge color="red">Disabled</Badge>
            ) : (
              <Badge color="green">Active</Badge>
            ),
        },
        {
          id: 'created_at',
          header: 'Created At',
          cell: (item) => new Date(item.created_at).toLocaleDateString(),
          sortingField: 'created_at',
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) => (
            <Button
              variant="inline-link"
              loading={disableLoading}
              onClick={() => disableUser(item.uid, !item.disabled_at)}
            >
              {item.disabled_at ? 'Enable' : 'Disable'}
            </Button>
          ),
        },
      ]}
      header={
        <Header counter={`(${users.length})`}>Users</Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search users"
        />
      }
      pagination={<Pagination {...paginationProps} />}
      empty={<Box textAlign="center">No users.</Box>}
    />
  )
}

// ─── Waitlist Tab ─────────────────────────────────────────────────────────────

function WaitlistTab() {
  const { waitlist, loading } = useWaitlist()
  const { approve, loading: approveLoading } = useApproveWaitlist()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const { items, paginationProps, collectionProps } = useCollection<WaitlistEntry>(
    waitlist,
    {
      filtering: {
        empty: <Box textAlign="center">No waitlist entries.</Box>,
        noMatch: <Box textAlign="center">No entries match the filter.</Box>,
      },
      pagination: { pageSize: isMobile ? 10 : 20 },
      sorting: {},
    },
  )

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        <Cards<WaitlistEntry>
          {...collectionProps}
          loading={loading}
          loadingText="Loading waitlist"
          items={items}
          cardsPerRow={[{ cards: 1 }]}
          header={<Header counter={`(${waitlist.length})`}>Waitlist</Header>}
          cardDefinition={{
            header: (item) => (
              <Box variant="strong">{item.email}</Box>
            ),
            sections: [
              {
                id: 'date',
                header: 'Requested At',
                content: (item) => new Date(item.requested_at).toLocaleDateString(),
              },
              {
                id: 'actions',
                header: 'Actions',
                content: (item) => (
                  <Button
                    variant="inline-link"
                    loading={approveLoading}
                    onClick={() => approve(item.docId, true)}
                  >
                    Approve
                  </Button>
                ),
              },
            ],
          }}
          empty={<Box textAlign="center">No waitlist entries.</Box>}
        />
        <Pagination {...paginationProps} />
      </SpaceBetween>
    )
  }

  return (
    <Table<WaitlistEntry>
      {...collectionProps}
      loading={loading}
      loadingText="Loading waitlist"
      items={items}
      columnDefinitions={[
        {
          id: 'email',
          header: 'Email',
          cell: (item) => item.email,
          sortingField: 'email',
        },
        {
          id: 'requested_at',
          header: 'Requested At',
          cell: (item) => new Date(item.requested_at).toLocaleDateString(),
          sortingField: 'requested_at',
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) => (
            <Button
              variant="inline-link"
              loading={approveLoading}
              onClick={() => approve(item.docId, true)}
            >
              Approve
            </Button>
          ),
        },
      ]}
      header={
        <Header counter={`(${waitlist.length})`}>Waitlist</Header>
      }
      pagination={<Pagination {...paginationProps} />}
      empty={<Box textAlign="center">No waitlist entries.</Box>}
    />
  )
}

// ─── System Prompts Tab ───────────────────────────────────────────────────────

function SystemPromptsTab() {
  const { systemPrompts, loading } = useSystemPrompts()
  const { upsert, loading: upsertLoading } = useUpsertSystemPrompt()
  const { deletePrompt, loading: deleteLoading } = useDeleteSystemPrompt()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const [editingPrompt, setEditingPrompt] = useState<SystemPrompt | null>(null)
  const [editContent, setEditContent] = useState('')
  const [deletingKey, setDeletingKey] = useState<string | null>(null)

  const { items, paginationProps, collectionProps } = useCollection<SystemPrompt>(
    systemPrompts,
    {
      filtering: {
        empty: <Box textAlign="center">No system prompts.</Box>,
        noMatch: <Box textAlign="center">No prompts match the filter.</Box>,
      },
      pagination: { pageSize: isMobile ? 10 : 20 },
      sorting: {},
    },
  )

  function handleEdit(prompt: SystemPrompt) {
    setEditingPrompt(prompt)
    setEditContent(prompt.content)
  }

  function handleCloseEdit() {
    setEditingPrompt(null)
    setEditContent('')
  }

  async function handleSave() {
    if (!editingPrompt) return
    await upsert(editingPrompt.key, editContent)
    handleCloseEdit()
  }

  function handleDeleteConfirm(key: string) {
    setDeletingKey(key)
  }

  function handleCloseDelete() {
    setDeletingKey(null)
  }

  async function handleDeleteExecute() {
    if (!deletingKey) return
    await deletePrompt(deletingKey)
    setDeletingKey(null)
  }

  const modals = (
    <>
      {/* Edit Modal */}
      <Modal
        visible={editingPrompt !== null}
        onDismiss={handleCloseEdit}
        header={`Edit: ${editingPrompt?.key ?? ''}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCloseEdit}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={upsertLoading}
                onClick={() => { void handleSave() }}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField label="Content">
          <Textarea
            value={editContent}
            onChange={({ detail }) => setEditContent(detail.value)}
            rows={10}
          />
        </FormField>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deletingKey !== null}
        onDismiss={handleCloseDelete}
        header="Confirm Delete"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCloseDelete}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={deleteLoading}
                onClick={() => { void handleDeleteExecute() }}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to delete the prompt &quot;{deletingKey}&quot;? This action cannot be undone.
        </Box>
      </Modal>
    </>
  )

  if (isMobile) {
    return (
      <>
        <SpaceBetween size="m">
          <Cards<SystemPrompt>
            {...collectionProps}
            loading={loading}
            loadingText="Loading system prompts"
            items={items}
            cardsPerRow={[{ cards: 1 }]}
            header={<Header counter={`(${systemPrompts.length})`}>System Prompts</Header>}
            cardDefinition={{
              header: (item) => (
                <Box variant="strong">{item.key}</Box>
              ),
              sections: [
                {
                  id: 'content',
                  header: 'Content',
                  content: (item) => (
                    <Box variant="span">
                      {item.content.length > 100
                        ? `${item.content.slice(0, 100)}…`
                        : item.content}
                    </Box>
                  ),
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  content: (item) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="inline-link"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="inline-link"
                        onClick={() => handleDeleteConfirm(item.key)}
                      >
                        Delete
                      </Button>
                    </SpaceBetween>
                  ),
                },
              ],
            }}
            empty={<Box textAlign="center">No system prompts.</Box>}
          />
          <Pagination {...paginationProps} />
        </SpaceBetween>
        {modals}
      </>
    )
  }

  return (
    <>
      <Table<SystemPrompt>
        {...collectionProps}
        loading={loading}
        loadingText="Loading system prompts"
        items={items}
        columnDefinitions={[
          {
            id: 'key',
            header: 'Type',
            cell: (item) => item.key,
            sortingField: 'key',
          },
          {
            id: 'content',
            header: 'Content',
            cell: (item) => (
              <Box variant="span">
                {item.content.length > 100
                  ? `${item.content.slice(0, 100)}…`
                  : item.content}
              </Box>
            ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="inline-link"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </Button>
                <Button
                  variant="inline-link"
                  onClick={() => handleDeleteConfirm(item.key)}
                >
                  Delete
                </Button>
              </SpaceBetween>
            ),
          },
        ]}
        header={
          <Header counter={`(${systemPrompts.length})`}>System Prompts</Header>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={<Box textAlign="center">No system prompts.</Box>}
      />
      {modals}
    </>
  )
}

// ─── AdminPage ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  return (
    <SpaceBetween size="l">
      <PageHeader title="Admin" />
      <Tabs
        tabs={[
          {
            id: 'users',
            label: 'Users',
            content: <UsersTab />,
          },
          {
            id: 'waitlist',
            label: 'Waitlist',
            content: <WaitlistTab />,
          },
          {
            id: 'system-prompts',
            label: 'System Prompts',
            content: <SystemPromptsTab />,
          },
        ]}
      />
    </SpaceBetween>
  )
}
